import { ArrowDown, ArrowUp, Check, Minus, Stethoscope } from 'lucide-react'
import { LAB_COPY } from '../i18n/modes/lab'
import type { LabTest, Language } from '../types/document'

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2))))

function rangeText(t: LabTest): string {
  if (t.low !== null && t.high !== null) return `${fmt(t.low)} – ${fmt(t.high)}`
  if (t.high !== null) return `< ${fmt(t.high)}`
  if (t.low !== null) return `> ${fmt(t.low)}`
  return ''
}

/**
 * Lab results as cards: each test, what it measures in plain words, the value
 * and where it sits against the range printed on the report. Out-of-range
 * results come first. Never a diagnosis.
 */
export function LabResults({ tests, language }: { tests: LabTest[]; language: Language }) {
  const copy = LAB_COPY[language] ?? LAB_COPY.en
  const order = { high: 0, low: 0, normal: 1 } as const
  const sorted = [...tests].sort((a, b) => (a.status ? order[a.status] : 2) - (b.status ? order[b.status] : 2))
  const statusLabel = (t: LabTest) =>
    t.status === 'high' ? copy.high : t.status === 'low' ? copy.low : t.status === 'normal' ? copy.normal : copy.noRange

  return (
    <section className="lab-results" aria-label={copy.title}>
      <h2>
        <Stethoscope size={20} aria-hidden="true" /> {copy.title}
      </h2>
      <ul className="lab-list">
        {sorted.map((t, i) => {
          const pos =
            t.low !== null && t.high !== null && t.high > t.low
              ? Math.max(0, Math.min(100, ((t.value - t.low) / (t.high - t.low)) * 60 + 20))
              : null
          const Icon = t.status === 'high' ? ArrowUp : t.status === 'low' ? ArrowDown : t.status === 'normal' ? Check : Minus
          return (
            <li key={i} className={`lab-item lab-${t.status ?? 'none'}`}>
              <div className="lab-head">
                <div>
                  <strong>{t.name}</strong>
                  {t.testId && <small>{copy.tests[t.testId]}</small>}
                </div>
                <div className="lab-value">
                  <span>{fmt(t.value)}</span> <small>{t.unit}</small>
                </div>
              </div>
              {pos !== null && (
                <div className="lab-bar" aria-hidden="true">
                  <span className="lab-band" />
                  <span className="lab-dot" style={{ left: `${pos}%` }} />
                </div>
              )}
              <p className="lab-status">
                <Icon size={16} aria-hidden="true" /> {statusLabel(t)}
                {rangeText(t) && (
                  <span className="lab-range">
                    {' '}· {copy.range}: {rangeText(t)} {t.unit}
                  </span>
                )}
              </p>
            </li>
          )
        })}
      </ul>
      <p className="lab-safety">{copy.safety}</p>
    </section>
  )
}
